import { AlertTriangle, CheckCircle2, Circle, Loader2, RefreshCw, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  type Rigenerazione,
  useM3RigenerazioneConAvanzamento,
  useM3RigeneraDocumento,
} from "@/hooks/useM3";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Barra e elenco dei documenti mentre il pacchetto si rigenera. */
function Avanzamento({ stato }: { stato: Rigenerazione }) {
  const totale = stato.documenti.length;
  const finiti = stato.documenti.filter(
    (d) => d.stato === "fatto" || d.stato === "non_uscito"
  ).length;
  const corrente = stato.documenti.find((d) => d.stato === "in_corso");
  const pct = stato.fase === "prepara" || !totale ? 0 : Math.round((finiti / totale) * 100);
  return (
    <div className="space-y-2.5" data-testid="avanzamento-rigenerazione">
      <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
        <span className="truncate font-medium text-foreground">
          {stato.fase === "prepara"
            ? "Preparo il pacchetto…"
            : corrente
              ? `Genero: ${corrente.label}`
              : stato.fase === "fatta"
                ? "Pacchetto rigenerato"
                : ""}
        </span>
        <span className="flex-none tabular-nums text-muted-foreground">
          {finiti}/{totale || "…"}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className="h-2 overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-brand-600 transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      {totale > 0 && (
        <ul className="space-y-1 text-[12px]">
          {stato.documenti.map((d) => (
            <li
              key={d.tipo}
              className={cn(
                "flex items-center gap-2",
                d.stato === "attesa" && "text-muted-foreground/70",
                d.stato === "in_corso" && "font-medium text-foreground",
                d.stato === "non_uscito" && "text-red-700"
              )}
            >
              {d.stato === "fatto" ? (
                <CheckCircle2 className="h-3.5 w-3.5 flex-none text-emerald-600" />
              ) : d.stato === "in_corso" ? (
                <Loader2 className="h-3.5 w-3.5 flex-none animate-spin text-brand-700" />
              ) : d.stato === "non_uscito" ? (
                <XCircle className="h-3.5 w-3.5 flex-none" />
              ) : (
                <Circle className="h-3.5 w-3.5 flex-none" />
              )}
              <span className="truncate">{d.label}</span>
              {d.stato === "non_uscito" && <span className="text-[11px]">— non è uscito</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Dialogo di rigenerazione (SPEC §5). Pacchetto: due opzioni «mantieni override
 * coerenti» / «riparti da zero». Documento: mantiene di default; se un override
 * non è più coerente con la NDP (409) chiede conferma «rigenera comunque».
 */
export function RigeneraDialog({
  scope,
  caricoId,
  tipo,
  label,
  onClose,
  onDone,
}: {
  scope: "pacchetto" | "documento";
  caricoId: string;
  tipo?: string;
  label?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const rigenerazione = useM3RigenerazioneConAvanzamento(caricoId);
  const rigDocumento = useM3RigeneraDocumento(caricoId, tipo ?? "");
  const [conflitto, setConflitto] = useState<string[] | null>(null);
  const { fase } = rigenerazione.stato;
  // Quando un cancello ferma la generazione il dialogo NON si chiude: senza
  // questo, premere «Rigenera» sembrava non fare niente.
  const bloccato =
    fase === "bloccata"
      ? rigenerazione.stato.messaggio ||
        "Le righe della merce non tornano con i totali della nota di prelievo."
      : null;
  const inCorso = fase === "prepara" || fase === "genera";
  const nonUsciti = rigenerazione.stato.documenti.filter((d) => d.stato === "non_uscito");

  const pending = inCorso || rigDocumento.isPending;

  function chiudi() {
    if (!pending) onClose();
  }

  // Il drawer passa callback nuove a ogni render: tenute in un ref, così il
  // timer di chiusura non riparte a ogni aggiornamento delle query.
  const chiusura = useRef({ onDone, onClose });
  useEffect(() => {
    chiusura.current = { onDone, onClose };
  });

  // Tutto uscito: si lascia vedere la barra piena un attimo, poi si chiude. Se
  // qualcosa non è uscito il dialogo resta aperto e lo dice.
  useEffect(() => {
    if (fase !== "fatta" || nonUsciti.length) return;
    const t = setTimeout(() => {
      chiusura.current.onDone();
      chiusura.current.onClose();
    }, 900);
    return () => clearTimeout(t);
  }, [fase, nonUsciti.length]);

  function rigeneraPacchetto(modalita: "mantieni" | "zero") {
    void rigenerazione.avvia(modalita);
  }

  function rigeneraDocumento(forza: boolean) {
    rigDocumento.mutate(
      { forza },
      {
        onSuccess: () => {
          onDone();
          onClose();
        },
        onError: (err) => {
          const body =
            err instanceof ApiError ? (err.body as { detail?: { chiavi?: string[] } }) : null;
          if (err instanceof ApiError && err.status === 409) {
            setConflitto(body?.detail?.chiavi ?? []);
          }
        },
      }
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && chiudi()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-brand-700" />
            {scope === "pacchetto" ? "Rigenera pacchetto" : `Rigenera «${label ?? tipo}»`}
          </DialogTitle>
        </DialogHeader>

        {scope === "pacchetto" ? (
          <div className="space-y-2 text-sm text-muted-foreground">
            {bloccato && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                <div>
                  <b>Non ho generato nessun documento.</b> {bloccato}. Controlla l'elenco della
                  merce qui sotto: correggilo o confermalo, e il pacchetto si rigenera da solo.
                </div>
              </div>
            )}
            {fase === "errore" && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                <div>
                  <b>La rigenerazione si è interrotta.</b> {rigenerazione.stato.messaggio}
                </div>
              </div>
            )}
            {(inCorso ||
              fase === "fatta" ||
              (fase === "errore" && rigenerazione.stato.documenti.length > 0)) && (
              <Avanzamento stato={rigenerazione.stato} />
            )}
            {fase === "fatta" && nonUsciti.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                <div>
                  {nonUsciti.length === 1
                    ? "Un documento non è uscito"
                    : `${nonUsciti.length} documenti non sono usciti`}
                  : gli altri sono rigenerati. Riprova dal pulsante del singolo documento.
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        onDone();
                        onClose();
                      }}
                    >
                      Chiudi
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {!inCorso && fase !== "fatta" && (
              <>
                <p>
                  Il pacchetto verrà rigenerato con l'ultima versione dei template. Come trattare i
                  dati inseriti a mano (n. fattura, n. certificato, ecc.)?
                </p>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => rigeneraPacchetto("mantieni")}
                  className="w-full rounded-md border border-border p-3 text-left hover:border-brand-500 hover:bg-brand-50/40"
                >
                  <div className="text-[13px] font-semibold text-foreground">
                    Mantieni i dati inseriti
                  </div>
                  <div className="text-[11.5px]">
                    Riapplica gli override ancora coerenti con la NDP attuale; quelli legati a
                    entità non più presenti vengono scartati.
                  </div>
                </button>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => rigeneraPacchetto("zero")}
                  className="w-full rounded-md border border-border p-3 text-left hover:border-brand-500 hover:bg-brand-50/40"
                >
                  <div className="text-[13px] font-semibold text-foreground">Riparti da zero</div>
                  <div className="text-[11.5px]">
                    Scarta tutti i dati inseriti a mano e rigenera solo dalle sorgenti.
                  </div>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3 text-sm text-muted-foreground">
            {conflitto ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                <div>
                  Alcuni dati inseriti a mano non sono più coerenti con la NDP e verranno scartati
                  {conflitto.length ? `: ${conflitto.join(", ")}` : ""}. Rigenerare comunque?
                </div>
              </div>
            ) : (
              <p>
                Rigenera il documento con l'ultima versione del suo template, mantenendo i campi
                inseriti a mano.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={chiudi} disabled={pending}>
                Annulla
              </Button>
              <Button onClick={() => rigeneraDocumento(conflitto != null)} disabled={pending}>
                {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {conflitto ? "Rigenera comunque" : "Rigenera"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
